// TEMPORARY TESTING AID. To remove: delete this file, then delete the `import DevPanel` line and the
// `<DevPanel ... />` line in index.js. Nothing else depends on it. Shown only when the URL has ?dev=1.
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, ButtonBase, Typography } from '@mui/material';

import {
  SHRED_COLORS,
  SHRED_COPY,
  SHRED_DEMO_ME_PARAM,
  SHRED_DEMO_PARAM,
  SHRED_FONTS,
  SHRED_TV_PARAM,
} from './constants';
import { buildShareText, getRankChangeNotice } from './helpers';

const DEV_PARAM = 'dev';

const SOURCES = [
  { label: 'Real API', value: null },
  { label: 'Demo live', value: '1' },
  { label: 'Demo ended', value: 'ended' },
  { label: 'Demo upcoming', value: 'upcoming' },
];

const ME_MODES = [
  { label: 'Mid', value: 'mid' },
  { label: 'Top', value: 'top' },
  { label: 'Chasing', value: 'chasing' },
  { label: 'Rookie', value: 'new' },
  { label: 'Nobody', value: 'none' },
];

const SAMPLE_REGULAR = { rank: 9, bracket: 'regular' };
const SAMPLE_BETTER = { rank: 7, bracket: 'regular', qualified: true, improvementPct: 12.3 };

const Group = ({ title, children }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography sx={{ fontFamily: SHRED_FONTS.numeric, fontSize: 10, letterSpacing: '0.14em', color: SHRED_COLORS.muted, mb: 0.5 }}>
      {title}
    </Typography>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>{children}</Box>
  </Box>
);

const Pill = ({ active, onClick, children }) => (
  <ButtonBase
    onClick={onClick}
    aria-pressed={!!active}
    sx={{
      px: 1.25,
      py: 0.5,
      borderRadius: '999px',
      fontFamily: SHRED_FONTS.body,
      fontWeight: 600,
      fontSize: 12,
      color: active ? SHRED_COLORS.ink : SHRED_COLORS.text,
      background: active ? SHRED_COLORS.gold : 'rgba(255,255,255,0.08)',
      border: `1px solid ${active ? 'transparent' : SHRED_COLORS.line}`,
    }}
  >
    {children}
  </ButtonBase>
);

const DevPanel = ({ onConfetti, onNotice }) => {
  const { search, pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);
  const params = new URLSearchParams(search);

  if (!params.has(DEV_PARAM)) {
    return null;
  }

  const setParam = (key, value) => {
    const next = new URLSearchParams(search);
    if (value === null || value === undefined) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    navigate({ pathname, search: `?${next.toString()}` }, { replace: true });
  };

  const demo = params.get(SHRED_DEMO_PARAM);
  const me = params.get(SHRED_DEMO_ME_PARAM) || 'mid';
  const tv = !!params.get(SHRED_TV_PARAM);

  return (
    <Box sx={{ position: 'fixed', left: 12, bottom: { xs: 76, md: 12 }, zIndex: 2000, maxWidth: 'calc(100vw - 24px)' }}>
      {open ? (
        <Box
          sx={{
            width: 300,
            maxWidth: '100%',
            maxHeight: '70vh',
            overflowY: 'auto',
            p: 1.5,
            borderRadius: '12px',
            background: 'rgba(12,9,8,0.96)',
            border: `1px solid ${SHRED_COLORS.gold}66`,
            boxShadow: '0 10px 30px rgba(0,0,0,0.6)',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography sx={{ fontFamily: SHRED_FONTS.display, fontWeight: 800, fontSize: 18, color: SHRED_COLORS.gold }}>
              DEV PANEL
            </Typography>
            <Pill onClick={() => setOpen(false)}>Hide</Pill>
          </Box>

          <Group title="DATA SOURCE">
            {SOURCES.map((source) => (
              <Pill key={source.label} active={demo === source.value} onClick={() => setParam(SHRED_DEMO_PARAM, source.value)}>
                {source.label}
              </Pill>
            ))}
          </Group>

          <Group title={demo ? 'WHO AM I (demo)' : 'WHO AM I (real API uses your login)'}>
            {ME_MODES.map((mode) => (
              <Pill
                key={mode.value}
                active={me === mode.value}
                onClick={() => setParam(SHRED_DEMO_ME_PARAM, mode.value)}
              >
                {mode.label}
              </Pill>
            ))}
          </Group>

          <Group title="DISPLAY">
            <Pill active={!tv} onClick={() => setParam(SHRED_TV_PARAM, null)}>Normal</Pill>
            <Pill active={tv} onClick={() => setParam(SHRED_TV_PARAM, '1')}>TV mode</Pill>
          </Group>

          <Group title="TRIGGER">
            <Pill onClick={onConfetti}>Confetti</Pill>
            <Pill onClick={() => onNotice(getRankChangeNotice(SAMPLE_REGULAR, SAMPLE_BETTER))}>Toast: moved up</Pill>
            <Pill onClick={() => onNotice(getRankChangeNotice(SAMPLE_BETTER, SAMPLE_REGULAR))}>Toast: dropped</Pill>
            <Pill onClick={() => onNotice(buildShareText(SHRED_COPY.SHARE_TEXT, SAMPLE_BETTER))}>Share text</Pill>
          </Group>

          <Typography sx={{ fontFamily: SHRED_FONTS.numeric, fontSize: 10, color: SHRED_COLORS.muted, wordBreak: 'break-all' }}>
            {pathname}{search}
          </Typography>
          <Typography sx={{ fontFamily: SHRED_FONTS.body, fontSize: 11, color: SHRED_COLORS.muted, mt: 0.5 }}>
            Replay: use the day scrubber on the page. Remove this panel by deleting DevPanel.js and its two lines in index.js.
          </Typography>
        </Box>
      ) : (
        <Pill active onClick={() => setOpen(true)}>DEV</Pill>
      )}
    </Box>
  );
};

export default DevPanel;
